import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Chip,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { auditLogAPI } from '../../services/api';
import { AuditLogEntry } from '../../types';

const actionColors: Record<string, 'success' | 'info' | 'error' | 'default'> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'error',
};

const AuditLog = () => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState({ entityType: '', username: '', action: '' });
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; entry: AuditLogEntry | null }>({ open: false, entry: null });

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = { page: page + 1, pageSize: rowsPerPage };
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.username) params.username = filters.username;
      if (filters.action) params.action = filters.action;
      const response = await auditLogAPI.getAll(params);
      setEntries(response.data.data); setTotal(response.data.total); setError('');
    } catch (err) { setError('Failed to load audit log'); } finally { setLoading(false); }
  }, [page, rowsPerPage, filters]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { auditLogAPI.getEntityTypes().then((res) => setEntityTypes(res.data)).catch(() => {}); }, []);

  const handleFilterChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
    setPage(0);
  };

  const formatJson = (json: string | null) => {
    if (!json) return 'null';
    try { const parsed = typeof json === 'string' ? JSON.parse(json) : json; return JSON.stringify(parsed, null, 2); }
    catch { return String(json); }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4">Audit Log</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Track all administrative changes (180-day retention)</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField select label="Entity Type" value={filters.entityType} onChange={handleFilterChange('entityType')} size="small" sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {entityTypes.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
        </TextField>
        <TextField label="Username" value={filters.username} onChange={handleFilterChange('username')} size="small" sx={{ minWidth: 160 }} />
        <TextField select label="Action" value={filters.action} onChange={handleFilterChange('action')} size="small" sx={{ minWidth: 120 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="CREATE">CREATE</MenuItem><MenuItem value="UPDATE">UPDATE</MenuItem><MenuItem value="DELETE">DELETE</MenuItem>
        </TextField>
      </Paper>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Timestamp</TableCell><TableCell>User</TableCell><TableCell>Action</TableCell><TableCell>Entity Type</TableCell><TableCell>Entity ID</TableCell><TableCell align="right">Details</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.AuditLogId}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(entry.CreatedAt + 'Z').toLocaleString()}</TableCell>
                <TableCell>{entry.Username}</TableCell>
                <TableCell><Chip label={entry.Action} color={actionColors[entry.Action] || 'default'} size="small" /></TableCell>
                <TableCell>{entry.EntityType}</TableCell><TableCell>{entry.EntityId || '-'}</TableCell>
                <TableCell align="right"><IconButton size="small" onClick={() => setDetailDialog({ open: true, entry })}><ViewIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!loading && entries.length === 0 && (<TableRow><TableCell colSpan={6} align="center">No audit log entries found</TableCell></TableRow>)}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} onPageChange={(_e, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />
      </TableContainer>
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, entry: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Audit Log Detail
          {detailDialog.entry && (<Typography variant="body2" color="text.secondary">{detailDialog.entry.Action} {detailDialog.entry.EntityType} #{detailDialog.entry.EntityId} by {detailDialog.entry.Username}</Typography>)}
        </DialogTitle>
        <DialogContent>
          {detailDialog.entry && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Old Values</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>{formatJson(detailDialog.entry.OldValues)}</Paper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>New Values</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>{formatJson(detailDialog.entry.NewValues)}</Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog({ open: false, entry: null })}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLog;
